/* eslint-disable */
import { useState, useContext, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GlobalProvider } from "context/GlobalContext";
import * as url from "helper/UrlHelper";
import { postData } from "utils/Gateway";
import {
  getUserBrowser,
  getPublicIP,
  getTimeStamp,
} from "helper/AuthHelper/AuthHelper";
import { ecosystemSlug } from "Config/Config";

// Login component
const Login = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  // Context setters for global state
  const { setcreateAccountMessage, setuserEmailforOtp, setVerifyModuleName } =
    useContext(GlobalProvider);
  
  // Detect session expiry: userInfo present but token missing means interceptor cleared the session
  const isSessionExpired = localStorage.getItem("userInfo") && !localStorage.getItem("token");


  // Form state management
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    otpEmail: "",
  });

  // Validation state
  const [validation, setValidation] = useState({
    emailWarning: false,
    passwordWarning: false,
    otpEmailWarning: false,
    notValidEmail: false,
    notValidOtpEmail: false,
  });

  // UI state management
  const [uiState, setUiState] = useState({
    isLoading: false,
    otpSending: false,
    showPassword: false,
    loginFailed: false,
    errorMessage: "",
  });

  // Email validation helper
  const validateEmail = (email) =>
    /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/.test(email);

  // Reset form and validation states
  const resetForm = useCallback(() => {
    setFormData({ email: "", password: "", otpEmail: "" });
    setValidation({
      emailWarning: false,
      passwordWarning: false,
      otpEmailWarning: false,
      notValidEmail: false,
      notValidOtpEmail: false,
    });
    setUiState((prev) => ({ ...prev, errorMessage: "", loginFailed: false }));
    setcreateAccountMessage("");
    setuserEmailforOtp("");
  }, [setcreateAccountMessage, setuserEmailforOtp]);

  // Handle input changes and reset warnings for changed field
  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

    // Reset validation for changed field
    if (field === "email") {
      setValidation((prev) => ({
        ...prev,
        emailWarning: false,
        notValidEmail: false,
      }));
    }
    if (field === "otpEmail") {
      setValidation((prev) => ({
        ...prev,
        otpEmailWarning: false,
        notValidOtpEmail: false,
      }));
    }
    if (field === "password") {
      setValidation((prev) => ({
        ...prev,
        passwordWarning: false,
      }));
    }

    setUiState((prev) => ({ ...prev, errorMessage: "", loginFailed: false }));
  };

  // Toggle password visibility
  const togglePasswordVisibility = () =>
    setUiState((prev) => ({ ...prev, showPassword: !prev.showPassword }));

  // Handle OTP request logic
  const handleOtpRequest = async () => {
    const { otpEmail } = formData;
    const newValidation = { ...validation };

    // Validate OTP email input
    if (!otpEmail) {
      newValidation.otpEmailWarning = true;
    } else if (!validateEmail(otpEmail)) {
      newValidation.notValidOtpEmail = true;
    }

    setValidation(newValidation);
    if (newValidation.otpEmailWarning || newValidation.notValidOtpEmail) return;

    try {
      setUiState((prev) => ({ ...prev, otpSending: true }));

      // Get user's public IP for request
      const userPublicIp = await getPublicIP();
      const requestURL = `${url.API_BASE_URL}${url.API_GET_OTP}?ecosystemslug=${ecosystemSlug}`;
      const response = await postData(requestURL, {
        userip: userPublicIp,
        email: otpEmail,
      });

      // Handle OTP response
      if (response.status) {
        setuserEmailforOtp(otpEmail);
        setVerifyModuleName("OTP");
        history.push("/admin/verifyemail");
      } else {
        setUiState((prev) => ({
          ...prev,
          errorMessage: response.message,
        }));
      }
    } catch (error) {
      // Log error and show generic message
      console.error("OTP Request Error:", error);
      setUiState((prev) => ({
        ...prev,
        errorMessage: t("request_failed"),
      }));
    } finally {
      setUiState((prev) => ({ ...prev, otpSending: false }));
    }
  };

  // Handle login logic
  const handleLogin = async (e) => {
    e.preventDefault();
    const { email, password } = formData;
    const newValidation = { ...validation };
    let isValid = true;

    // Validate email and password fields
    if (!email) {
      newValidation.emailWarning = true;
      isValid = false;
    } else if (!validateEmail(email)) {
      newValidation.notValidEmail = true;
      isValid = false;
    }

    if (!password) {
      newValidation.passwordWarning = true;
      isValid = false;
    }

    setValidation(newValidation);
    if (!isValid) return;

    try {
      setUiState((prev) => ({ ...prev, isLoading: true }));

      // Get browser and IP info for login request
      const [detectedBrowser, userPublicIp] = await Promise.all([
        getUserBrowser(),
        getPublicIP(),
      ]);

      const { loginDate, loginTime, loginTimeStamp } = getTimeStamp();
      const requestURL = `${url.API_BASE_URL}${url.API_LOGIN}?ecosystemslug=${ecosystemSlug}`;

      // Send login request
      const response = await postData(requestURL, {
        email,
        password,
        logindate: loginDate,
        logintime: loginTime,
        logintimestamp: loginTimeStamp,
        browser: detectedBrowser,
        userip: userPublicIp,
      });

      console.log("Login Response:", response); // Debug log for login response

      // Handle login response
      if (response.status) {
        localStorage.setItem("token", response.token);
        localStorage.setItem("userInfo", JSON.stringify(response.data));
        localStorage.setItem("userID", response.data._id);
        localStorage.setItem("ecosystem", response.ecosystemid);
        localStorage.setItem("ecosystemslug", response.ecosystemslug);
        localStorage.setItem("ecosystemlogo", response.ecosystemlogo);
        localStorage.setItem("moduleaccess", response.data.moduleaccessdata);
        localStorage.setItem("language", response.data.language || "es");
        i18n.changeLanguage(response.data.language || "es");

        resetForm();
        window.location.reload();
      } else {
        setUiState((prev) => ({
          ...prev,
          errorMessage: response.message,
          loginFailed: true,
        }));
      }
    } catch (error) {
      // Log error and show generic message
      console.error("Login Error:", error);
      setUiState((prev) => ({
        ...prev,
        errorMessage: t("login_failed"),
      }));
    } finally {
      setUiState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  // Error message component
  const ErrorMessage = () =>
    uiState.errorMessage ? (
      <div className="validation-error d-flex align-items-center gap-1 fs-xs text-primary mt-2">
        <span className="d-block material-symbols-outlined icon-md">info</span>
        <span>{uiState.errorMessage}</span>
      </div>
    ) : null;

  // Create account link component
  const CreateAccountLink = () => (
    <div className="create_login_outer d-flex align-items-center">
      <div className="trms_check me-2">
        <label className="radio-check">
          <p>{t("Do not have an account")}?</p>
        </label>
      </div>
      <div className="apply_btn">
        <Link to="/admin/register" className="text-primary">
          {t("Create One")}
        </Link>
      </div>
    </div>
  );

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="mx-2 spinner-border spinner-border-sm" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  );

  // Main render
  return (
    <main id="app">
      <div id="content_wrapper">
        <div className="authentication-wrapper min-h-screen py-5 d-flex align-items-center justify-content-center">
          <div className="authentication-container px-4 py-5 mw-100 bg-white rounded-10 shadow-sm mx-auto">
            <h1 className="h2 text-center">{t("Login to your account")}</h1>

            {isSessionExpired && (
              <div className="alert alert-warning d-flex align-items-center gap-2 mt-3 mb-0 py-2 px-3 rounded-8 fs-sm">
                <span className="material-symbols-outlined icon-md">
                  warning
                </span>
                <span>
                  {t(
                    "Your session has expired. Please log in again to continue."
                  )}
                </span>
              </div>
            )}
            
            <form onSubmit={handleLogin}>
              {/* Tab Navigation */}
              <div className="login_tabs">
                <ul className="nav nav-tabs list_stye_none">
                  <li className="nav-item w-50 m-0 text-center">
                    <Link
                      className="nav-link active show"
                      data-bs-toggle="tab"
                      to="#password"
                      onClick={resetForm}
                    >
                      {t("Password")}
                    </Link>
                  </li>
                  <li className="nav-item w-50 m-0 text-center">
                    <Link
                      className="nav-link"
                      data-bs-toggle="tab"
                      to="#otp"
                      onClick={resetForm}
                    >
                      {t("OTP")}
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {/* Password Tab */}
                <div className="tab-pane fade active show" id="password">
                  <div className="form-group mb-4">
                    <label className="d-block fs-sm fw-semibold mb-2">
                      {t("Email")}
                    </label>
                    <input
                      type="email"
                      className={`form-control fs-sm shadow-none mb-2 ${
                        validation.notValidEmail ? "is-invalid" : ""
                      }`}
                      placeholder={t("Enter email")}
                      value={formData.email}
                      onChange={handleInputChange("email")}
                    />
                    {/* Email validation messages */}
                    {validation.emailWarning && (
                      <div className="validation-error d-flex align-items-center gap-1 fs-xs text-primary mt-2">
                        <span className="d-block material-symbols-outlined icon-md">
                          info
                        </span>
                        <span>{t("Please enter email")}</span>
                      </div>
                    )}
                    {validation.notValidEmail && (
                      <div className="validation-error d-flex align-items-center gap-1 fs-xs text-primary mt-2">
                        <span className="d-block material-symbols-outlined icon-md">
                          info
                        </span>
                        <span>{t("Please enter valid email")}</span>
                      </div>
                    )}

                    <div className="form-group mb-4">
                      <label className="d-block fs-sm fw-semibold mb-2">
                        {t("Password")}
                      </label>
                      <div className="field-container field-with-icon icon-right position-relative">
                        <input
                          type={uiState.showPassword ? "text" : "password"}
                          className={`form-control fs-sm shadow-none ${
                            validation.passwordWarning ? "is-invalid" : ""
                          }`}
                          placeholder={t("Enter Password")}
                          value={formData.password}
                          onChange={handleInputChange("password")}
                        />
                        {/* Password visibility toggle */}
                        <button
                          type="button"
                          className="password-visibility field-icon text-gray position-absolute top-50 end-0 translate-middle-y me-3 bg-transparent border-0"
                          onClick={togglePasswordVisibility}
                        >
                          <span className="d-block material-symbols-outlined icon-sm">
                            visibility
                          </span>
                        </button>
                      </div>
                      {/* Password validation message */}
                      {validation.passwordWarning && (
                        <div className="validation-error d-flex align-items-center gap-1 fs-xs text-primary mt-2">
                          <span className="d-block material-symbols-outlined icon-md">
                            info
                          </span>
                          <span>{t("Please enter password")}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Error message display */}
                  <ErrorMessage />

                  <div className="form-group d-flex align-items-center justify-content-end gap-3 mb-4">
                    <Link
                      to="/admin/forgotpassword"
                      className="fs-sm fw-semibold text-primary"
                    >
                      {t("Forgot Password")}
                    </Link>
                  </div>

                  <div className="action d-flex gap-3 justify-content-between">
                    <CreateAccountLink />
                    <button
                      className="btn btn-primary"
                      onClick={handleLogin}
                      disabled={uiState.isLoading}
                    >
                      {t("Login")}
                      {uiState.isLoading && <LoadingSpinner />}
                    </button>
                  </div>
                </div>

                {/* OTP Tab */}
                <div className="tab-pane fade" id="otp">
                  <div className="form-group mb-4">
                    <label className="d-block fs-sm fw-semibold mb-2">
                      {t("Email")}
                    </label>
                    <input
                      type="email"
                      className={`form-control fs-sm shadow-none ${
                        validation.notValidOtpEmail ? "is-invalid" : ""
                      }`}
                      placeholder={t("Enter email")}
                      value={formData.otpEmail}
                      onChange={handleInputChange("otpEmail")}
                    />
                    {/* OTP email validation messages */}
                    {validation.otpEmailWarning && (
                      <div className="validation-error d-flex align-items-center gap-1 fs-xs text-primary mt-2">
                        <span className="d-block material-symbols-outlined icon-md">
                          info
                        </span>
                        <span>{t("Please enter email")}</span>
                      </div>
                    )}
                    {validation.notValidOtpEmail && (
                      <div className="validation-error d-flex align-items-center gap-1 fs-xs text-primary mt-2">
                        <span className="d-block material-symbols-outlined icon-md">
                          info
                        </span>
                        <span>{t("Please enter valid email")}</span>
                      </div>
                    )}
                  </div>

                  {/* Error message display */}
                  <ErrorMessage />

                  <div className="action d-flex gap-3 justify-content-between">
                    <CreateAccountLink />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleOtpRequest}
                      disabled={uiState.otpSending}
                    >
                      {t("Get OTP")}
                      {uiState.otpSending && <LoadingSpinner />}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Login;
